import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { LuCircleCheck, LuExternalLink, LuLock, LuTrophy } from 'react-icons/lu';
import { WorkspaceLayout } from '../workspace/WorkspaceLayout';
import { WorkspaceHeader } from '../workspace/WorkspaceHeader';
import { WorkspaceSkeleton } from '../workspace/WorkspaceSkeleton';
import { StatementPane } from '../workspace/StatementPane';
import { ProblemStatement } from '../workspace/ProblemStatement';
import { CodeEditor } from '../workspace/CodeEditor';
import { RunConsole } from '../workspace/RunConsole';
import { RunSubmitButtons } from '../workspace/RunSubmitButtons';
import { SubmissionHistory } from '../workspace/SubmissionHistory';
import { useBoundaryReload } from './useBoundaryReload';
import { Button, ButtonLink } from '../../ui/Button';
import { EmptyState, Skeleton } from '../../ui/Feedback';
import { Meter } from '../../ui/Labels';
import { useToast } from '../../ui/Toast';
import { celebrate } from '../../ui/Celebration';
import { cx } from '../../ui/cx';
import { useDocumentTitle, useStrings } from '../../tenant/TenantProvider';
import { useCodeWorkspace } from '../../hooks/useCodeWorkspace';
import { useNow } from '../../hooks/useNow';
import { contestApi } from '../../lib/contestApi';
import { formatCountdown } from '../../lib/format';

const RANK_REFRESH_MS = 60_000;

function Gate({ contest, now }) {
  const t = useStrings();
  let icon = LuLock;
  let title;
  let text;
  if (contest.state === 'upcoming') {
    title = t('arena.gateUpcomingTitle', { time: formatCountdown(Date.parse(contest.startTime) - now) });
    text = contest.registered ? t('arena.gateRegistered') : t('arena.gateNotRegistered');
  } else if (contest.state === 'live') {
    title = t('arena.gateLateTitle');
    text = t('arena.gateLateText');
  } else if (contest.state === 'ended') {
    title = t('arena.gateClosedTitle');
    text = t('arena.gateClosedText');
  } else if (contest.state === 'finalized') {
    icon = LuTrophy;
    title = t('arena.gateFinalTitle');
    text = t('arena.gateFinalText');
  } else {
    title = t('arena.gateCancelledTitle');
    text = t('contest.cancelledHint');
  }
  return (
    <div className="not-found">
      <EmptyState
        icon={icon}
        title={title}
        text={text}
        action={
          <div className="gate-actions">
            <ButtonLink to={`/contests/${contest.id}`} variant="primary">
              {t('arena.contestPage')}
            </ButtonLink>
            {contest.state !== 'upcoming' && contest.state !== 'cancelled' && (
              <ButtonLink to={`/contests/${contest.id}/standings`}>{t('arena.standings')}</ButtonLink>
            )}
          </div>
        }
      />
    </div>
  );
}

function ProblemSwitcher({ problems, activeId, onSelect }) {
  const t = useStrings();
  return (
    <nav className="problem-switcher" aria-label={t('contest.problemsTitle')}>
      {problems.map((problem) => (
        <button
          key={problem.id}
          type="button"
          className={cx('problem-switcher__item', problem.solved && 'is-solved', problem.attempted && !problem.solved && 'is-attempted')}
          aria-current={problem.id === activeId ? 'true' : undefined}
          title={`${problem.label}. ${problem.title} · ${problem.points}`}
          onClick={() => onSelect(problem.id)}
        >
          {problem.solved && <LuCircleCheck aria-hidden="true" />}
          {problem.label}
        </button>
      ))}
    </nav>
  );
}

function ContestClock({ contest, now, live }) {
  const t = useStrings();
  const start = Date.parse(contest.startTime);
  const end = Date.parse(contest.endTime);
  const remaining = end - now;
  const urgent = live && remaining <= 5 * 60_000;
  return (
    <div className={cx('contest-clock', urgent && 'is-urgent')} aria-label={t('arena.timeLeft')}>
      <span className="contest-clock__time mono">{live ? formatCountdown(remaining) : t('arena.over')}</span>
      {live && <Meter value={Math.max(0, remaining)} max={end - start} label={t('arena.timeLeft')} />}
    </div>
  );
}

export function ContestArena() {
  const { contestId } = useParams();
  const [params, setParams] = useSearchParams();
  const t = useStrings();
  const { show } = useToast();
  const now = useNow(1000);
  const [state, setState] = useState({ status: 'loading', contest: null, problems: [], error: null });
  const [details, setDetails] = useState({});
  const [detailError, setDetailError] = useState(null);
  const [panelTab, setPanelTab] = useState('description');
  const [rank, setRank] = useState(null);

  const loadContest = useCallback(async () => {
    const response = await contestApi.detail(contestId);
    if (!response.success) {
      setState({ status: 'error', contest: null, problems: [], error: response.status === 404 ? t('contest.notFound') : response.error });
      return;
    }
    const { contest, problems } = response.data;
    setState({
      status: problems && contest.state !== 'finalized' ? 'ready' : 'locked',
      contest,
      problems: problems ?? [],
      error: null,
    });
  }, [contestId, t]);

  useEffect(() => {
    loadContest();
  }, [loadContest]);

  const contest = state.contest;
  useDocumentTitle(contest?.name ?? t('contests.title'));

  const endMs = contest ? Date.parse(contest.endTime) : 0;
  const isLive = contest?.state === 'live' && now < endMs;
  const canAct = Boolean(isLive && contest?.registered);
  const boundary = contest ? (contest.state === 'upcoming' ? Date.parse(contest.startTime) : contest.state === 'live' ? endMs : null) : null;
  useBoundaryReload(boundary, now, loadContest);

  const activeId = useMemo(() => {
    const requested = Number(params.get('problem'));
    if (state.problems.some((problem) => problem.id === requested)) return requested;
    return state.problems[0]?.id ?? null;
  }, [params, state.problems]);
  const activeProblem = activeId !== null ? details[activeId] : undefined;

  useEffect(() => {
    if (state.status !== 'ready' || activeId === null || details[activeId]) return undefined;
    let cancelled = false;
    setDetailError(null);
    contestApi.problem(contestId, activeId).then((response) => {
      if (cancelled) return;
      if (response.success) setDetails((current) => ({ ...current, [activeId]: response.data.problem }));
      else setDetailError(response.error);
    });
    return () => {
      cancelled = true;
    };
  }, [contestId, state.status, activeId, details]);

  useEffect(() => {
    if (state.status !== 'ready') return undefined;
    let cancelled = false;
    const fetchRank = async () => {
      const response = await contestApi.leaderboard(contestId, 1);
      if (!cancelled && response.success) setRank(response.data.me?.rank ?? null);
    };
    fetchRank();
    const timer = setInterval(fetchRank, RANK_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [contestId, state.status]);

  const handleJudged = (problemId, result) => {
    const accepted = result.verdict === 'Accepted';
    setState((current) => ({
      ...current,
      problems: current.problems.map((problem) =>
        problem.id === problemId ? { ...problem, attempted: true, solved: problem.solved || accepted } : problem
      ),
    }));
    if (accepted) {
      const label = state.problems.find((problem) => problem.id === problemId)?.label ?? '';
      celebrate(document.querySelector('[data-celebrate-origin]'));
      show(t('arena.acceptedToast', { label }));
    }
  };

  const api = useMemo(
    () => ({
      run: (problemId, body) => contestApi.run(contestId, problemId, body),
      runStatus: contestApi.runStatus,
      submit: (problemId, body) => contestApi.submit(contestId, problemId, body),
      submissionStatus: contestApi.submissionStatus,
    }),
    [contestId]
  );

  const workspace = useCodeWorkspace({
    draftPrefix: `contest:${contestId}`,
    problemId: activeId,
    problem: activeProblem,
    canAct,
    now,
    api,
    onRequestError: (response) => {
      if (response.code === 'CONTEST_NOT_LIVE') loadContest();
    },
    onJudged: handleJudged,
  });

  const loadSubmissions = useCallback(() => contestApi.submissions(contestId, activeId), [contestId, activeId]);

  if (state.status === 'loading') return <WorkspaceSkeleton />;

  if (state.status === 'error') {
    return (
      <div className="not-found">
        <EmptyState
          icon={LuTrophy}
          title={state.error}
          action={
            <div className="gate-actions">
              <Button onClick={loadContest}>{t('common.retry')}</Button>
              <ButtonLink to="/contests" variant="primary">
                {t('contest.back')}
              </ButtonLink>
            </div>
          }
        />
      </div>
    );
  }

  if (state.status === 'locked') return <Gate contest={contest} now={now} />;

  const notice = !isLive ? (
    <div className="workspace-notice workspace-notice--warning" role="status">
      {t('arena.endedBanner')}
      <Link to={`/contests/${contest.id}/standings`} className="text-link">
        {t('arena.standings')}
      </Link>
    </div>
  ) : !contest.registered ? (
    <div className="workspace-notice" role="status">
      {t('arena.viewOnly')}
    </div>
  ) : null;

  return (
    <WorkspaceLayout
      header={
        <WorkspaceHeader
          backTo={`/contests/${contest.id}`}
          backLabel={contest.name}
          actions={
            <>
              <ContestClock contest={contest} now={now} live={isLive} />
              <Link to={`/contests/${contest.id}/standings`} target="_blank" rel="noreferrer" className="arena-standings">
                {rank ? t('arena.rank', { rank }) : t('arena.standings')}
                <LuExternalLink aria-hidden="true" />
              </Link>
              <RunSubmitButtons
                canAct={canAct}
                running={workspace.running}
                submitting={workspace.judging}
                runCooldownMs={workspace.runCooldownMs}
                submitCooldownMs={workspace.submitCooldownMs}
                onRun={workspace.handleRun}
                onSubmit={workspace.handleSubmit}
              />
            </>
          }
        >
          <ProblemSwitcher problems={state.problems} activeId={activeId} onSelect={(id) => setParams({ problem: String(id) }, { replace: true })} />
        </WorkspaceHeader>
      }
      notice={notice}
      statement={
        <StatementPane tab={panelTab} onTabChange={setPanelTab}>
          {detailError && <p className="console-error">{detailError}</p>}
          {!detailError && !activeProblem && (
            <div className="history-skeleton">
              <Skeleton width="45%" height={22} />
              <Skeleton width="30%" />
              <Skeleton width="100%" />
              <Skeleton width="85%" />
            </div>
          )}
          {activeProblem && panelTab === 'description' && (
            <ProblemStatement
              number={`${activeProblem.label}.`}
              title={activeProblem.title}
              difficulty={activeProblem.difficulty}
              meta={[t('contests.pointCount', { count: activeProblem.points })]}
              solved={state.problems.find((problem) => problem.id === activeId)?.solved}
              description={activeProblem.description}
            />
          )}
          {activeProblem && panelTab === 'submissions' && <SubmissionHistory load={loadSubmissions} version={workspace.submissionsVersion} />}
        </StatementPane>
      }
      editor={
        <CodeEditor
          code={workspace.code}
          onChange={workspace.updateCode}
          language={workspace.language}
          languages={workspace.languages}
          onLanguageChange={workspace.changeLanguage}
          onReset={workspace.resetCode}
          onRun={workspace.handleRun}
          onSubmit={workspace.handleSubmit}
          readOnly={!activeProblem}
          savedAt={workspace.savedAt}
        />
      }
      console={
        <RunConsole
          testCases={workspace.testCases}
          onTestCasesChange={workspace.updateCases}
          onResetCases={workspace.resetCases}
          tab={workspace.consoleTab}
          onTabChange={workspace.setConsoleTab}
          run={workspace.run}
          submission={workspace.judgement}
          lastAction={workspace.lastAction}
          readOnly={!canAct}
          acceptedNote={t('arena.acceptedNote')}
        />
      }
    />
  );
}
